import { useEffect, useState, Fragment as RFragment } from "react";
import React from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { Plus, X, Search } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { DashboardAccessPanel } from "@/components/DashboardAccessPanel";
import { PermissionGuideModal } from "@/components/PermissionGuideModal";
import { RoleBulkPermissionsModal } from "@/components/RoleBulkPermissionsModal";

// Module list mirrors every routable feature in the app (sidebar).
// Each entry is `[key, label, section]`. Keys match the backend permission keys.
const MODULE_DEFS = [
  // Overview
  ["dashboard", "Dashboard", "Overview"],
  // Jun-2026 — Operations Dashboard: management view of all ops-enabled
  // projects (KPIs + searchable project table). ₹ additionally needs
  // boq_commercials.view; percentages always visible.
  ["ops_dashboard", "Operations Dashboard", "Overview"],
  ["approvals", "Approvals Inbox", "Overview"],
  // Masters
  ["projects", "Projects", "Masters"],
  // Sprint 2026-Feb — narrow stock-only project-visibility escape hatch.
  // When granted (`view` action), the holder can enumerate every
  // inventory batch across projects in their branch scope — needed for
  // store-keeper / warehouse-operator roles (Safety-Store, Consumable-
  // Store, etc.) who must issue material from stock but are NOT in
  // every project's `authorized_users` list. This does NOT grant
  // access to project master, transactions, P&L, allocations, or any
  // other project-scoped data — it is strictly a batch-picker unlock.
  // Backend check lives in auth_project.is_stock_project_access_unrestricted.
  ["inventory_all_projects", "View All Stock Batches (cross-project)", "Masters"],
  // Sprint 2 — Project Budget (per-category budget with revision history,
  // approval-gated). Backend module key `project_budget` (also configurable
  // in the Approval Matrix).
  ["project_budget", "Project Budgets", "Masters"],
  // Aug-2026 — Phase 1: Project Scope & BOQ. Action mapping: view = read
  // BOQ tab/versions/lines · create = upload file + commit draft/revision
  // + discard · edit = submit for approval · export = Excel export +
  // original-file download. Approval routing via Approval Matrix module
  // key `project_boq` (approve/reject governed by matrix membership).
  ["project_boq", "Project Scope & BOQ", "Masters"],
  // Separate commercial-visibility key (owner decision D-1): reveals Client
  // Rate / Budget Rate / Tax % / values ONLY when the holder also has
  // project_boq access. NEVER grants BOQ access by itself — the backend
  // strips commercial fields server-side without it.
  ["boq_commercials", "BOQ Commercials (Rates & Values)", "Masters"],
  // Aug-2026 — Phase 2A: Project Purchase Requisition. Actions: view = read
  // register/PRs · create = create draft · edit = edit own draft + submit ·
  // delete = cancel (incl. controlled Approved-PR cancel) · export = register
  // export. Approve/Reject via Approval Matrix key `project_purchase_requisition`.
  ["project_pr", "Project Purchase Requisition (PR)", "Masters"],
  // Separate PR commercial-visibility key: reveals estimated rates / tax /
  // values ONLY when the holder also has project_pr access. Never grants PR
  // access by itself (server-side field stripping).
  ["pr_commercials", "PR Commercials (Estimated Values)", "Masters"],
  // Jun-2026 — Phase 2B: Approved Project PR → existing Draft PO conversion.
  // view = eligibility + PR-vs-PO comparison · create = convert · delete =
  // controlled Draft-conversion cancel. Grants NOTHING else (PO approval, PI,
  // GRN, stock, assets and accounting stay with their own permissions).
  ["project_po_conversion", "Project PR → PO Conversion", "Masters"],
  // Jun-2026 — Phase 3C: Project GRN. view = register/GRNs · create = draft ·
  // edit = edit own draft + submit · delete = REVERSE approved GRN · approve =
  // posting recovery. Approve/Reject flow via Approval Matrix key `project_grn`.
  ["project_grn", "Project GRN (Goods Receipt)", "Masters"],
  // Read-only locked Project Stock view. view = tab/register · export = Excel.
  // NO mutation is possible through this module (server-enforced).
  ["project_stock", "Project Stock (Read-only)", "Masters"],
  // Jun-2026 — GRN-less Ops-Project opening stock (previous-ERP carry-in).
  // Placed next to Project Stock so admins find it where the button lives.
  ["project_opening_stock", "Project Opening Stock (prev. ERP)", "Masters"],
  // Separate commercial-visibility key: reveals provisional PO-rate values on
  // GRN/Project Stock ONLY. Never grants stock/GRN access by itself.
  ["project_stock_values", "Project Stock Provisional Values", "Masters"],
  // Jun-2026 — PMC-A: Project Material Consumption (daily site consumption of
  // locked Project GRN stock; SEPARATE from Issue Voucher). view = history/
  // register · create = record consumption (immediate post) · edit = posting
  // recovery re-run · delete = REVERSE a posted document · approve = BACKDATE
  // authority + project-period exception (NOT an approval flow — none exists)
  // · export = Excel · upload = attachments. Values additionally require
  // project_stock_values.view.
  ["project_material_consumption", "Project Material Consumption", "Masters"],
  // Org-wide PMC access: bypasses the project-assignment leg ONLY (branch
  // scope still applies). Never grants PMC actions by itself.
  ["project_material_consumption_all", "PMC — All Projects Access", "Masters"],
  // PST (Aug-2026) — raise/view project-to-project stock transfers.
  // Acceptance at the destination needs project access only (owner decision).
  ["project_stock_transfer", "Project Stock Transfer (between projects)", "Masters"],
  // Jun-2026 — WP-A: Daily BOQ Work Progress (records executed quantities
  // against the current Approved BOQ baseline; operational only, no GL).
  // view = lines/register/summary · create = daily entry (immediate post) ·
  // delete = REVERSE an entry (append-only) · approve = BACKDATE authority +
  // project-period exception · export = Excel. Commercial values (rates /
  // work amounts / BOQ values) additionally require boq_commercials.view.
  ["work_progress", "Work Progress (Daily BOQ)", "Masters"],
  // Org-wide WP access: bypasses the project-assignment leg ONLY (branch
  // scope still applies). Never grants WP actions by itself.
  ["work_progress_all", "Work Progress — All Projects Access", "Masters"],
  // Jun-2026 — WP-B: Labour Library (central labour master; Aadhaar always
  // masked by default). view = list/detail · create = new labour · edit =
  // update fields + manage skill/type masters · inactivate = status changes ·
  // Full Aadhaar additionally requires view_full_aadhaar.
  ["labour_library", "Labour Library", "Masters"],
  // Assign/release labourers to ONE active project at a time. view =
  // assignment history + project panel · assign · release.
  ["labour_assignment", "Labour Project Assignment", "Masters"],
  // Jun-2026 — WP-C1: daily labour attendance inside Work Progress. view =
  // eligible list / sheets / history · create = mark today's attendance ·
  // edit = append-only corrections · export = Excel. Backdating additionally
  // requires the granular `backdate` action (extra row below).
  ["labour_attendance", "Labour Attendance", "Masters"],
  // Org-wide attendance access: bypasses the project-assignment leg ONLY
  // (branch scope still applies). Never grants attendance actions by itself.
  ["labour_attendance_all", "Labour Attendance — All Projects Access", "Masters"],
  // Jun-2026 — Labour Payments: day-wise wage register + payment requests
  // (approval-matrix routed). view = register/requests · create = raise a
  // request · edit = override per-day amount · post_payment = mark Paid ·
  // export = Excel. ZERO GL — operational payment record only.
  ["labour_payment", "Labour Payments & Wage Requests", "Masters"],
  // Jun-2026 — WP-C2: Daily Progress Report. view = previews/history ·
  // create = draft · granular extras: edit_draft / submit (freeze + PDF) /
  // revise (new revision) / download (PDF) / backdate.
  ["project_dpr", "Daily Progress Report (DPR)", "Masters"],
  // Org-wide DPR access: bypasses project assignment ONLY (branch scope
  // still applies). Never grants DPR actions by itself.
  ["project_dpr_all", "DPR — All Projects Access", "Masters"],
  // Admin-manageable blocker categories used inside DPRs (granular `manage`).
  ["dpr_blocker_master", "DPR Blocker Master", "Masters"],
  // Dedicated Aadhaar reveal key (F0 catalog): view = see/reveal the full
  // 12-digit Aadhaar (audited, purpose mandatory) · export = full-Aadhaar
  // Excel export. Without it everything shows XXXXXXXX1234.
  ["view_full_aadhaar", "View Full Aadhaar (Audited)", "Masters"],
  // Jun-2026 — Phase 4: Project PO → existing PI integration. view = see
  // PI-ready Project POs in the PI picker/panel · create = pull them into a
  // PI · approve = the explicit per-PO PI-READINESS action (NOT PI approval).
  ["project_po_pi", "Project PO → PI (Readiness & Pull)", "Masters"],
  // PO–GRN–PI comparison panel + informational three-way match (read-only).
  // Rate/value figures additionally require project_stock_values.view.
  ["project_pi_match", "PO–GRN–PI Match Panel", "Masters"],
  // create = allowed to record the mandatory reason when PI quantity runs
  // ahead of accepted GRN quantity (audited exception).
  ["project_pi_exception", "PI-ahead-of-GRN Exception", "Masters"],
  // Jun-2026 — read-only payment trail (PI → Bill Payment → Payment Voucher)
  // against project POs, inside the Vendor Management tab. ₹ values follow
  // pr_commercials masking.
  ["po_payments", "PO Payments (Vendor Tab)", "Masters"],
  // Jun-2026 — payment request workflow against Approved project POs:
  // create = raise a request (pick Approved GRNs + route to a same-branch
  // active user) · view = see the requests inbox / payable GRNs. Assignee
  // (any role) completes by linking the created PI, or rejects with reason.
  ["po_payment_request", "PO Payment Request (Vendor Tab)", "Masters"],
  // Jun-2026 — per-project DPR mail distribution: manage = edit the On/Off
  // switch + Name/Email recipient list on the Work Progress → DPR tab.
  // The DPR PDF is auto-emailed on every submit/revision when On.
  ["dpr_mail_config", "DPR Mail Config", "Masters"],
  ["clients", "Clients", "Masters"],
  ["vendors", "Vendors", "Masters"],
  ["members", "Members", "Masters"],
  ["items", "Items", "Masters"],
  // Sprint 3.6 — Bank Ledger master (lets admins onboard / manage bank
  // accounts used for receipts, payments, and reconciliation). Controls
  // sidebar visibility of /masters/bank-ledger.
  ["bank_ledger", "Bank Ledger", "Masters"],
  // Inventory
  ["inventory", "Stock / Inventory", "Inventory & Assets"],
  ["assets", "Assets", "Inventory & Assets"],
  // Sub-modules under Inventory & Assets — surfaced as separate
  // permission keys so admins can grant sidebar visibility per voucher
  // type. Backend functional access on these is still gated by the
  // umbrella `inventory` / `assets` keys (create/edit/delete) so a user
  // who can SEE a voucher type also needs the umbrella perm to act on
  // it. Layout.jsx falls back to the umbrella when the specific key
  // isn't granted, so legacy roles continue to work unchanged.
  ["issue_voucher", "Issue Voucher", "Inventory & Assets"],
  ["return_voucher", "Return Voucher", "Inventory & Assets"],
  ["returnable_register", "Returnable Register", "Inventory & Assets"],
  ["stock_transfer", "Stock Transfer", "Inventory & Assets"],
  // Sprint 2.2 — Material Transfer Voucher (project → project stock move,
  // no consumption / no P&L hit). Gates sidebar visibility and CRUD on
  // /material-transfers; the approval flow is configured separately via
  // the Approval Matrix module key `material_transfer_voucher`.
  ["material_transfer", "Material Transfer Voucher", "Inventory & Assets"],
  // Sprint 4.3 — Inter-warehouse stock move within the same branch.
  // Controls sidebar visibility of /warehouse-transfers + CRUD on the
  // warehouse-transfer endpoints.
  ["warehouse_transfer", "Warehouse Transfer", "Inventory & Assets"],
  // Sprint 4.3 — Read-only Warehouse Movement Ledger (audit trail of
  // every PI, IV, RV, MTV, WHT, Repair movement). Controls sidebar
  // visibility of /warehouse-movements.
  ["warehouse_movements", "WH Movement Ledger", "Inventory & Assets"],
  // Sprint 4.11.12 — Stock Adjustment Voucher (manual stock correction,
  // approval-gated). Controls sidebar visibility of /stock-adjustments
  // + per-action gating on the SAV CRUD endpoints. `approve` action
  // additionally guards the admin Cancel-Approval (reverse) endpoint.
  ["stock_adjustment", "Stock Adjustment Voucher", "Inventory & Assets"],
  // Jul-2026 — Batch Scrap Sale voucher (own key so admins can control who
  // may view / raise / export scrap sales, independent of the umbrella
  // `assets` key). Approval routing is configured via the Approval Matrix
  // module key `scrap_sale`.
  ["scrap_sale", "Scrap Sale Voucher", "Inventory & Assets"],
  ["depreciation", "Depreciation", "Inventory & Assets"],
  // Transactions — Sales
  ["estimates", "Estimates", "Transactions"],
  ["approved_estimate_queue", "Approved Estimate Queue", "Transactions"],
  ["jms", "Job Sheet (JMS)", "Transactions"],
  ["sales_invoices", "Sales Invoices", "Transactions"],
  ["sales_credit_note", "Sales Credit Note", "Transactions"],
  ["sales_debit_note", "Sales Debit Note", "Transactions"],
  ["payment_receipt", "Payment Receipt", "Transactions"],
  // Jun-2026 — OEM Complaint: client-site vendor complaint register with
  // chase mails. view = list/figures/mail history · create = manual entry +
  // bulk upload · edit = tag vendor no., Client Scope, resolve, send now ·
  // delete = hard-delete an OPEN complaint (mail history wiped, ticket reusable).
  ["oem_complaints", "OEM Complaint", "Transactions"],
  // Transactions — Purchase / Payments
  ["purchase_order", "Purchase Order", "Transactions"],
  // Jun-2026 (user request) — who may RAISE a post-approval PO cancellation
  // (grant "create"); the cancellation then flows the po_cancellation
  // approval matrix.
  ["po_cancellation", "PO Cancellation (Post-Approval)", "Transactions"],
  ["purchase_invoices", "Purchase Invoices", "Transactions"],
  ["expenses", "Expense Vouchers", "Transactions"],
  ["payments", "Payments (Bill / Post / Store / Salary)", "Transactions"],
  ["cash_bank", "Cash / Bank", "Transactions"],
  // Approvals
  // Inbox of pending approval requests across all modules. Controls
  // sidebar visibility of /approvals. View / Approve / Reject are gated
  // by the per-module approval matrix on the backend, but this key
  // gates the sidebar item itself.
  ["approvals", "Approvals Inbox", "Approvals"],
  // Sprint 2026-Feb — ONE (Official Note Entry) module. Digital
  // replacement for paper approval notes with L1..L5 sequential
  // approvals, queries, and Policy-only revisions.
  ["one", "Official Note Entry (ONE)", "Approvals"],
  // GST & Compliance
  ["gst", "GST / E-Invoice / E-Way Bill", "GST & Compliance"],
  // Accounting
  ["ledgers", "Ledgers", "Accounting"],
  ["opening_balance", "Opening Balance Wizard", "Accounting"],
  ["trial_balance", "Trial Balance", "Accounting"],
  ["general_ledger", "General Ledger", "Accounting"],
  // Sprint 2026-Feb — Journal Voucher. Each category has its own
  // permission module (kept internally separate) even though the UI
  // collapses them into a single "Journal Voucher" screen.
  ["journal_voucher_eb", "Journal Voucher · Employee Benefit Expenses", "Accounting"],
  ["journal_voucher_fc", "Journal Voucher · Finance Cost", "Accounting"],
  ["journal_voucher_oe", "Journal Voucher · Other Expenses", "Accounting"],
  // Jun-2026 (user-reported gap) — OI category existed on the JV screen but
  // had no permission key here, so admins couldn't tag users for it.
  ["journal_voucher_oi", "Journal Voucher · Other Income", "Accounting"],
  // Sprint 2026-Feb JV Stabilization — Ledger Master (canonical chart
  // of accounts used exclusively by Journal Vouchers).
  ["jv_ledger_master", "JV Ledger Master", "Accounting"],
  // Jul-2026 — statutory/accounting reports got their OWN keys (they
  // previously piggy-backed on the broad `reports` umbrella, which made
  // them visible to every role with reports.view).
  ["gl_audit", "GL Integrity Audit", "Accounting"],
  ["financial_integrity", "Financial Integrity Certificate", "Accounting"],
  ["balance_sheet", "Balance Sheet (Schedule III)", "Accounting"],
  ["profit_loss", "Profit & Loss (Schedule III)", "Accounting"],
  // Documents & Reports
  ["documents", "Documents", "Documents & Reports"],
  ["reports", "Reports", "Documents & Reports"],
  ["audit", "Audit Reports", "Documents & Reports"],
  // Sprint 3 #2 — Project Health Dashboard (cross-project budget health
  // rollup, Net Commitment & Transfer Map). Controls sidebar visibility
  // of /project-health.
  ["project_health", "Project Health Dashboard", "Documents & Reports"],
  // Sprint 3 #3 — Cross-Project Audit Reports (MTV+IV+RV trail, In/Out
  // registers, Movement History, Excel audit trail). Gated separately
  // from the umbrella `reports` so Finance / Internal Audit can be given
  // access to the audit reports without seeing the full reports catalog.
  ["cross_project_audit", "Cross-Project Audit Reports", "Documents & Reports"],
  // Sprint 4.11 — Financial rollup dashboards. Each has its own key so
  // an admin can grant a Finance Analyst view-access to (say) only
  // Project P&L without exposing Company / Branch consolidations. The
  // sidebar visibility logic falls back to the umbrella `reports` key
  // so existing users keep working without re-configuration.
  ["project_pnl",             "Project P&L",              "Documents & Reports"],
  // Jun-2026 — previous-ERP opening balances (P&L-only; no accounting impact).
  ["project_opening_balance", "Opening Balances (prev. ERP)", "Documents & Reports"],
  ["branch_pnl",              "Branch P&L",               "Documents & Reports"],
  ["vertical_pnl",            "Vertical P&L",             "Documents & Reports"],
  ["company_pnl",             "Company P&L",              "Documents & Reports"],
  ["warehouse_inventory_report", "Warehouse Inventory Report", "Documents & Reports"],
  ["cost_centre_expense",     "Cost Centre Expense Report", "Documents & Reports"],
  ["issued_statement",        "Issued Statement (PDF)",   "Documents & Reports"],
  // Sprint 2026-Feb-12 — Estimate Pending Ageing Summary (Client POC Wise).
  // Own permission key so admins can grant/revoke access independently
  // of the broader `reports` umbrella. Falls back to `reports.view` in
  // the sidebar rule so legacy roles remain unaffected.
  ["estimate_ageing_report",  "Estimate Ageing Summary",  "Documents & Reports"],
  // Jul-2026 — consolidated QR scan audit across Issue / Return / WT vouchers.
  ["scan_activity_report",    "Scan Activity Report",     "Documents & Reports"],
  // Jul-2026 — Identity Audit Sweep: frozen beneficiary integrity across
  // all historical BPVs + Vendor Advances (view/export).
  ["identity_audit",          "Identity Audit Sweep",     "Documents & Reports"],
  ["issued_items_report",     "Issued Items Report",      "Documents & Reports"],
  ["returnable_report",       "Returnable Tracking",      "Documents & Reports"],
  ["gst_centre",              "GST Centre",               "Documents & Reports"],
  // Sprint 2026-Q1e — Financial Control Centre (integrity dashboard).
  // Its own key so admins can restrict who sees the FCC + exception
  // triage without hiding the main Dashboard.
  ["financial_control_centre", "Financial Control Centre", "Documents & Reports"],
  // Sprint MA-R1 — Overhead Allocation Engine (Rules Master, Basis
  // Snapshots, Run Workbench). Gated separately from the Cost Centre
  // Master (which still uses `management_accounting`) so admins can
  // decide which Finance user can *only view* Cost Centres vs who can
  // actually author allocation rules and fire runs.
  ["overhead_allocation",     "Overhead Allocation",      "Documents & Reports"],
  // Sprint 2026-Jun — VoltLedger integration Phase B. Receivables +
  // Compliance module keys registered so roles can be pre-configured;
  // sidebar entries ship with each module's page in later phases.
  ["debtor_dashboard",          "Debtor/HR Management Dashboard", "Receivables"],
  ["invoice_queue",             "Invoice Queue (Follow-up)",   "Receivables"],
  ["bulk_receipts",             "Bulk Receipts",               "Receivables"],
  ["receipt_voucher_register",  "Receipt Voucher Register",    "Receivables"],
  // Jul-2026 — TDS Recovery Report: TDS deducted by clients per invoice
  // with Form 16A certificate tracking (view / export / edit-to-mark).
  ["tds_recovery_report",       "TDS Recovery Report",         "Receivables"],
  ["receivable_retentions",     "Retentions (Receivable)",     "Receivables"],
  ["receivable_status_options", "Receivable Status Master",    "Receivables"],
  // Phase F — Receipt Vouchers group standard Payment Receipts; the Due Date
  // Editor is a permission-controlled utility with a full audit trail.
  ["due_date_editor",           "Due Date Editor",             "Receivables"],
  ["aging_outstanding_report",  "Aging Outstanding Report",    "Documents & Reports"],
  ["receivables_reports",       "Receivables Reports",         "Documents & Reports"],
  ["performance_pnl",           "Project Monthly Performance P&L", "Documents & Reports"],
  ["hr_compliance",             "HR Compliance",               "Compliance"],
  // Administration
  ["users", "Users & Roles", "Administration"],
  ["approval_matrix", "Approval Matrix", "Administration"],
  ["company_settings", "Company Setup", "Administration"],
  ["gst_config", "GST E-Invoice API", "Administration"],
  ["financial_year", "Financial Year", "Administration"],
  ["branches", "Branches", "Administration"],
  ["item_groups", "Item Groups", "Administration"],
  // Sprint 2026-Jun — Project Groups got its own key (was piggy-backing
  // on the broad `projects` key, which leaked the admin library to every
  // role with projects.view).
  ["project_groups", "Project Groups", "Administration"],
  ["hsn_sac", "HSN / SAC Library", "Administration"],
  ["uom", "UOM Library", "Administration"],
  ["numbering", "Numbering Series", "Administration"],
  // Cost Centre Master (post MA-R1 · Feb 2026) — moved from a
  // dedicated "Management Accounting" sidebar group to Administration.
  // The permission key `management_accounting` is retained so existing
  // user permission grants keep working without migration.
  ["management_accounting", "Cost Centres", "Administration"],
  // Aug-2026 — pre-deployment permission-catalog audit: these keys were
  // backend-enforced but never grantable in this matrix (only admins could
  // ever use the features). Registered following the existing convention.
  ["warehouses", "Warehouse Master", "Administration"],
  ["email_config", "Email Configuration", "Administration"],
  ["estimate_email", "Estimate Email (send estimates)", "Transactions"],
  ["estimate_reminder", "Estimate Payment Reminders", "Transactions"],
  ["project_ops_admin", "Project Operations Settings", "Administration"],
  // Data Tools — exports, bulk-import templates, system maintenance.
  // Admin-only sidebar item but kept in the matrix for completeness and
  // forward-compat (e.g., when a finance-power-user role is introduced).
  ["data_tools", "Data Tools", "Administration"],
];
const MODULES = MODULE_DEFS.map(([k]) => k);
const SECTIONS = [...new Set(MODULE_DEFS.map(([, , s]) => s))];

// Voucher numbering modules — these unlock the per-module "manual override"
// permission on the Lock/Unlock voucher-number input across the app.
// Order matches the typical Sales → Purchase → Payments → Inventory flow.
const NUMBERING_MODULES = [
  ["estimate", "Estimate"],
  ["sales_invoice", "Sales Invoice"],
  ["sales_credit_note", "Sales Credit Note"],
  ["sales_debit_note", "Sales Debit Note"],
  ["payment_receipt", "Payment Receipt"],
  ["receipt_voucher", "Receipt Voucher (Bulk Receipts)"],
  ["purchase_order", "Purchase Order"],
  ["purchase_invoice", "Purchase Invoice"],
  ["expense_voucher", "Expense Voucher"],
  ["bill_payment_voucher", "Bill Payment Voucher"],
  ["batch_payment", "Batch Payment"],
  ["vendor_advance", "Vendor Advance"],
  ["store_rent", "Store Rent"],
  ["salary_payment", "Salary Payment"],
  ["general_payment", "General Payment"],
  ["issue_voucher", "Material Issue Voucher"],
  ["return_voucher", "Return Voucher"],
  ["stock_transfer", "Stock Transfer"],
  // Sprint 2.2 — Material Transfer Voucher numbering series.
  ["material_transfer_voucher", "Material Transfer Voucher"],
  ["asset_transfer", "Asset Transfer"],
  ["asset_repair", "Asset Repair"],
  ["asset_scrap", "Asset Scrap"],
  // Jul-2026 — Batch Scrap Sale numbering series.
  ["scrap_sale", "Scrap Sale (Batch)"],
  // Sprint 4.11.12 — Stock Adjustment Voucher numbering series.
  ["stock_adjustment", "Stock Adjustment Voucher"],
];
// IMPORTANT: every action name here is also a backend `require_permission`
// gate. Adding/removing an entry MUST be paired with `STANDARD_ACTIONS` in
// `backend/auth.py` (line ~971). `post_payment` is a special action used only
// by the Payments module (controls who can finalise a NEFT batch / mark-paid).
const ACTIONS = ["view", "create", "edit", "delete", "approve", "export", "upload", "post_payment"];

// Pretty labels for the column headers (kebab/snake → Title-Case-with-spaces).
const ACTION_LABELS = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  approve: "Approve",
  export: "Export",
  upload: "Upload",
  post_payment: "Post Payment",
};

// Sprint 2026-Jul — module-specific GRANULAR actions rendered as an extra
// row under the module in the permission matrix (backend gates:
// require_permission("invoice_queue", "<action>")).
const MODULE_EXTRA_ACTIONS = {
  invoice_queue: ["update_status", "update_jobsheet", "override_due_date", "manage_masters"],
  performance_pnl: ["edit_after_approval"],
  labour_attendance: ["backdate", "view_wages"],
  project_boq: ["view_sales_invoices"],
  project_dpr: ["edit_draft", "submit", "revise", "download", "backdate"],
  dpr_blocker_master: ["manage"],
  // Aug-2026 — pre-deployment audit: backend gates on these non-standard
  // actions; without the rows here the permissions were un-grantable.
  dpr_mail_config: ["manage"],
  email_config: ["manage"],
  estimate_email: ["send"],
  estimate_reminder: ["manage"],
};
const EXTRA_ACTION_LABELS = {
  view_sales_invoices: "View Sales Invoices",
  update_status: "Update Status",
  update_jobsheet: "Update Jobsheet",
  override_due_date: "Override Due Date",
  manage_masters: "Manage Masters",
  edit_after_approval: "Edit Reporting Period (Post-Approval)",
  backdate: "Backdate",
  view_wages: "View Wages & Estimates",
  edit_draft: "Edit Draft",
  submit: "Submit / Finalize",
  revise: "Revise",
  download: "Download PDF",
  manage: "Manage",
  send: "Send Email",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tab, setTab] = useState("users");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [roleOpen, setRoleOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [roleQuery, setRoleQuery] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    // Sprint 2026-Feb — use Promise.allSettled so a permission gap on
    // one endpoint doesn't blank out both the users AND roles tables.
    // Previously the parent's `Promise.all([/users, /roles])` failed
    // fully whenever `/users` returned 403, which is exactly what
    // happened for a non-admin role (e.g. Accounts) with `users.view`
    // — the roles dropdown on the New User modal came up empty even
    // though `/roles` succeeded. Now each setter runs independently.
    const [u, r] = await Promise.allSettled([api.get("/users"), api.get("/roles")]);
    setUsers(u.status === "fulfilled" ? (u.value.data || []) : []);
    setRoles(r.status === "fulfilled" ? (r.value.data || []) : []);
  };
  useEffect(() => { load(); }, []);

  async function deleteUser(id) {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Administration"
        title="Users & Roles"
        subtitle="Manage team access with dynamic role-based permissions."
        actions={tab === "users" ? (
          <button data-testid="add-user-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New User
          </button>
        ) : tab === "roles" ? (
          <div className="flex items-center gap-2">
            <button data-testid="access-guide-btn" onClick={() => setGuideOpen(true)} className="bg-white border border-slate-300 hover:border-[#F97316] text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold">
              Access Guide
            </button>
            <button data-testid="bulk-permissions-btn" onClick={() => setBulkOpen(true)} className="bg-[#1A1D21] hover:bg-black text-white px-3 py-2.5 text-xs uppercase tracking-wider font-bold">
              Bulk Permissions
            </button>
            <button data-testid="add-role-btn" onClick={() => { setEditingRole(null); setRoleOpen(true); }} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New Role
            </button>
          </div>
        ) : null}
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[["users", "Users"], ["roles", "Roles & Permissions"], ["dashboard_access", "Dashboard Access"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      {/* Search bar (per tab) */}
      {tab !== "dashboard_access" && (
      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        {tab === "users" ? (
          <input
            data-testid="user-search"
            value={userQuery}
            onChange={e => setUserQuery(e.target.value)}
            placeholder="Search users by name, email or role…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none"
          />
        ) : (
          <input
            data-testid="role-search"
            value={roleQuery}
            onChange={e => setRoleQuery(e.target.value)}
            placeholder="Search roles by name or description…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none"
          />
        )}
      </div>
      )}

      {tab === "dashboard_access" ? <DashboardAccessPanel /> : tab === "users" ? (
        (() => {
          const q = userQuery.trim().toLowerCase();
          const filteredUsers = !q ? users : users.filter(u =>
            (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q)
          );
          return filteredUsers.length === 0 ? <EmptyState title={q ? "No matching users" : "No users yet"} /> : (
          <Card className="overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Name", "Email", "Role", "Status", ""].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-5">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-5 text-sm font-medium text-slate-800">{u.name}</td>
                    <td className="py-3 px-5 text-sm text-slate-700">{u.email}</td>
                    <td className="py-3 px-5"><span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5">{u.role}</span></td>
                    <td className="py-3 px-5 text-xs"><span className={u.active ? "text-emerald-700" : "text-red-600"}>{u.active ? "● Active" : "● Disabled"}</span></td>
                    <td className="py-3 px-5 text-xs whitespace-nowrap">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => { setEditing(u); setOpen(true); }} className="text-[#F97316] hover:underline mr-3">Edit</button>
                      <button data-testid={`copy-user-${u.id}`} onClick={() => { setEditing({ ...u, _copy: true, id: undefined, email: "", password: "", name: `${u.name || ""} (Copy)` }); setOpen(true); }} className="text-blue-700 hover:underline mr-3">Copy</button>
                      {u.role !== "admin" && <button onClick={() => deleteUser(u.id)} className="text-red-600 hover:underline">Delete</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          );
        })()
      ) : (
        (() => {
          const q = roleQuery.trim().toLowerCase();
          const filteredRoles = !q ? roles : roles.filter(r =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.description || "").toLowerCase().includes(q)
          );
          return (
        <div className="space-y-4">
          {filteredRoles.map(r => (
            <Card key={r.id} className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display font-bold text-slate-900 uppercase">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.description}</div>
                </div>
                {!r.is_system || r.name === "admin" ? null : (
                  <button onClick={() => { setEditingRole(r); setRoleOpen(true); }} className="text-[#F97316] text-xs uppercase tracking-wider hover:underline">Edit Perms</button>
                )}
                {!r.is_system && (
                  <button onClick={() => { setEditingRole(r); setRoleOpen(true); }} className="text-[#F97316] text-xs uppercase tracking-wider hover:underline">Edit Perms</button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(r.permissions || {}).map(([mod, acts]) => (
                  <span key={mod} className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5">
                    {mod}: {Array.isArray(acts) ? acts.join(", ") : "—"}
                  </span>
                ))}
              </div>
            </Card>
          ))}
        </div>
          );
        })()
      )}

      {open && <UserModal user={editing} roles={roles} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {roleOpen && <RoleModal role={editingRole} onClose={() => setRoleOpen(false)} onSaved={() => { setRoleOpen(false); load(); }} />}
      {guideOpen && <PermissionGuideModal onClose={() => setGuideOpen(false)} />}
      {bulkOpen && <RoleBulkPermissionsModal roles={roles} onClose={() => setBulkOpen(false)} onSaved={load} />}
    </div>
  );
}

function UserModal({ user, roles, onClose, onSaved }) {
  // Sprint 4.5 — `_copy` is a transient marker set when the operator
  // clicks "Copy". Treat it like a new-user form so email/password are
  // required, but pre-fill permissions / branch / warehouse picks from
  // the source row.
  const isCopy = !!(user && user._copy);
  const isEdit = !!(user && !isCopy && user.id);
  const initial = user
    ? { ...user, _copy: undefined, id: isEdit ? user.id : undefined }
    : { email: "", password: "", name: "", role: "viewer", active: true,
        allowed_branch_ids: [], allowed_warehouse_ids: [], permissions: {} };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  // Sprint 4.5 — branch-scoped warehouse multi-select
  const [warehouses, setWarehouses] = useState([]);
  // 'all_in_branch' = empty allowed_warehouse_ids ⇒ all WHs under picked branches.
  // 'selected'      = explicit list.
  const initialWhMode = (initial.allowed_warehouse_ids && initial.allowed_warehouse_ids.length)
    ? "selected" : "all_in_branch";
  const [whMode, setWhMode] = useState(initialWhMode);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Per-role numbering grants — used to mark which checkboxes are inherited.
  const roleObj = roles.find(r => r.name === form.role) || {};
  const roleNumbering = (roleObj.permissions || {}).numbering_override || {};
  const userNumbering = (form.permissions || {}).numbering_override || {};

  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data || [])).catch(() => {});
  }, []);

  // Sprint 4.5 — Load all warehouses ONCE; filter client-side by the
  // user's currently-picked branches. Auto-prune any warehouse picks
  // that no longer belong to a picked branch (e.g. operator unticks
  // a branch).
  useEffect(() => {
    api.get("/warehouses/picker").then(({ data }) => {
      const wlist = Array.isArray(data) ? data : (data?.items || []);
      setWarehouses(wlist);
    }).catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    // Cross-branch invariant: drop WH picks that are no longer in scope.
    const inScope = new Set(
      warehouses
        .filter(w => (form.allowed_branch_ids || []).includes(w.branch_id))
        .map(w => w.id)
    );
    const before = form.allowed_warehouse_ids || [];
    const after  = before.filter(id => inScope.has(id));
    if (after.length !== before.length) {
      setForm(p => ({ ...p, allowed_warehouse_ids: after }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.allowed_branch_ids, warehouses]);

  function toggleBranch(bid) {
    const cur = new Set(form.allowed_branch_ids || []);
    if (cur.has(bid)) cur.delete(bid); else cur.add(bid);
    set("allowed_branch_ids", Array.from(cur));
  }

  function toggleWarehouse(wid) {
    const cur = new Set(form.allowed_warehouse_ids || []);
    if (cur.has(wid)) cur.delete(wid); else cur.add(wid);
    set("allowed_warehouse_ids", Array.from(cur));
  }

  async function save(e) {
    e.preventDefault();
    const picked = (form.allowed_branch_ids || []).filter(Boolean);
    if (picked.length === 0) {
      toast.error("Please select at least one branch under 'Allowed Branches'.");
      return;
    }
    // Sprint 4.5 — Warehouse mode determines the payload.
    //   • all_in_branch  → empty array (= "all warehouses in selected branches")
    //   • selected       → explicit list of warehouse ids
    const allowedWh = (whMode === "all_in_branch")
      ? []
      : (form.allowed_warehouse_ids || []).filter(Boolean);
    if (whMode === "selected" && allowedWh.length === 0) {
      toast.error("Selected Warehouses mode requires at least one warehouse pick.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name, role: form.role, active: form.active,
          allowed_branch_ids: picked,
          allowed_warehouse_ids: allowedWh,
          permissions: form.permissions || {},
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${form.id || user.id}`, payload);
      } else {
        await api.post("/users", {
          email: form.email, password: form.password, name: form.name, role: form.role,
          allowed_branch_ids: picked,
          allowed_warehouse_ids: allowedWh,
          permissions: form.permissions || {},
        });
      }
      toast.success(isCopy ? "User copied successfully" : "Saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-display text-xl font-bold">
            {isEdit ? "Edit User" : (isCopy ? "Copy User — new account" : "New User")}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          {/* Basic identity + auth — 2 cols on tablet, 4 cols on desktop so all
              of {Name · Email · Password · Role} fit on a single row.
              Keeps the modal short vertically (user's 2026-02-22 ask). */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Name *"><input data-testid="user-name" required value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} /></Field>
            <Field label="Email *"><input data-testid="user-email" type="email" required disabled={isEdit} value={form.email} onChange={e => set("email", e.target.value)} className={inputCls + " disabled:bg-slate-100"} /></Field>
            <Field label={isEdit ? "New Password (leave blank to keep)" : "Password *"}>
              <input data-testid="user-password" type="password" required={!isEdit} value={form.password || ""} onChange={e => set("password", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Role">
              <select data-testid="user-role" value={form.role} onChange={e => set("role", e.target.value)} className={inputCls}>
                {roles.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
              </select>
            </Field>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} />
              <span>Account active</span>
            </label>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                Allowed Branches <span className="text-rose-600">*</span>
                <span className="ml-2 text-slate-400 normal-case font-normal tracking-normal">
                  (at least one branch is required)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="user-branch-select-all"
                  onClick={() => set("allowed_branch_ids", branches.map(b => b.id))}
                  className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline">
                  Select All
                </button>
                <span className="text-slate-300">·</span>
                <button type="button" data-testid="user-branch-clear-all"
                  onClick={() => set("allowed_branch_ids", [])}
                  className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:underline">
                  Clear
                </button>
              </div>
            </div>
            {/* 3-col grid (was 2) since the modal is now ~2× wider */}
            <div className={`border p-2 max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 ${
              (form.allowed_branch_ids || []).length === 0 ? "border-rose-300 bg-rose-50/30" : "border-slate-200"
            }`} data-testid="user-branch-multi">
              {branches.length === 0 && <div className="text-xs text-slate-400 col-span-4 text-center py-2">No branches yet</div>}
              {branches.map(b => (
                <label key={b.id} className="flex items-center gap-2 text-xs hover:bg-slate-50 px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={(form.allowed_branch_ids || []).includes(b.id)}
                    onChange={() => toggleBranch(b.id)} data-testid={`user-branch-${b.branch_code}`} />
                  <span className="font-mono text-slate-500">{b.branch_code}</span>
                  <span className="text-slate-800">{b.branch_name}</span>
                </label>
              ))}
            </div>
            {(form.allowed_branch_ids || []).length === 0 && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="user-branch-required-msg">
                Tick at least one branch above — branch access is mandatory.
              </div>
            )}
          </div>

          {/* Sprint 4.5 — Warehouse Access (branch-scoped multi-select) */}
          <div data-testid="user-warehouse-access-block">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                Warehouse Access
                <span className="ml-2 text-slate-400 normal-case font-normal tracking-normal">
                  (warehouses are nested under branches — pick the access mode)
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1 border border-slate-200 hover:bg-slate-50"
                data-testid="wh-mode-all">
                <input type="radio" name="whMode" value="all_in_branch"
                  checked={whMode === "all_in_branch"}
                  onChange={() => { setWhMode("all_in_branch"); set("allowed_warehouse_ids", []); }} />
                <span><b>All Warehouses</b> under assigned branches</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1 border border-slate-200 hover:bg-slate-50"
                data-testid="wh-mode-selected">
                <input type="radio" name="whMode" value="selected"
                  checked={whMode === "selected"}
                  onChange={() => setWhMode("selected")} />
                <span><b>Selected Warehouses</b> only</span>
              </label>
            </div>
            {whMode === "selected" && (
              <>
                <div className="flex items-center justify-end gap-2 mb-1">
                  <button type="button" data-testid="user-wh-select-all-in-scope"
                    onClick={() => {
                      const inScope = warehouses
                        .filter(w => (form.allowed_branch_ids || []).includes(w.branch_id))
                        .map(w => w.id);
                      set("allowed_warehouse_ids", inScope);
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline">
                    Select All In Scope
                  </button>
                  <span className="text-slate-300">·</span>
                  <button type="button" data-testid="user-wh-clear-all"
                    onClick={() => set("allowed_warehouse_ids", [])}
                    className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:underline">
                    Clear
                  </button>
                </div>
                <div className={`border p-2 max-h-48 overflow-y-auto ${
                  (form.allowed_warehouse_ids || []).length === 0
                    ? "border-rose-300 bg-rose-50/30" : "border-slate-200"
                }`} data-testid="user-warehouse-multi">
                  {(form.allowed_branch_ids || []).length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-2">
                      Pick at least one branch above to see its warehouses.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(form.allowed_branch_ids || []).map(bid => {
                        const branch = branches.find(b => b.id === bid);
                        const branchWarehouses = warehouses.filter(w => w.branch_id === bid);
                        if (branchWarehouses.length === 0) return null;
                        return (
                          <div key={bid}>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                              {branch?.branch_code} — {branch?.branch_name}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                              {branchWarehouses.map(w => (
                                <label key={w.id} className="flex items-center gap-2 text-xs hover:bg-slate-50 px-2 py-1 cursor-pointer">
                                  <input type="checkbox"
                                    checked={(form.allowed_warehouse_ids || []).includes(w.id)}
                                    onChange={() => toggleWarehouse(w.id)}
                                    data-testid={`user-wh-${w.warehouse_code}`} />
                                  <span className="font-mono text-slate-500">{w.warehouse_code}</span>
                                  <span className="text-slate-800 truncate">{w.warehouse_name}</span>
                                  {w.is_default && <span className="text-[9px] text-emerald-700 font-bold">DEFAULT</span>}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {(form.allowed_warehouse_ids || []).length === 0 && (form.allowed_branch_ids || []).length > 0 && (
                  <div className="text-[11px] text-rose-600 mt-1">
                    Pick at least one warehouse — Selected mode cannot be empty.
                  </div>
                )}
              </>
            )}
            {whMode === "all_in_branch" && (
              <div className="text-[11px] text-slate-500 italic px-2 py-1 bg-emerald-50/60 border border-emerald-200">
                User will have access to every warehouse under their {(form.allowed_branch_ids || []).length} selected branch{(form.allowed_branch_ids || []).length === 1 ? "" : "es"}.
              </div>
            )}
          </div>

          {/* ---- Per-user Numbering Override exceptions ---- */}
          {form.role !== "admin" && (
            <div className="border border-amber-200 bg-amber-50/30">
              <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Voucher Numbering Override — Per-User Exceptions</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  Inherited from role <span className="font-mono bg-white px-1 border border-slate-200">{form.role}</span>.
                  Tick extra boxes here to grant this user override access for additional voucher types beyond what their role allows.
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 px-3 py-2">
                {NUMBERING_MODULES.map(([k, label]) => {
                  const fromRole = !!roleNumbering[k];
                  const fromUser = !!userNumbering[k];
                  const effective = fromRole || fromUser;
                  return (
                    <label key={k} className={`flex items-center gap-2 text-xs px-2 py-1 cursor-pointer ${fromRole ? "opacity-80" : "hover:bg-amber-50/60"}`}>
                      <input
                        type="checkbox"
                        data-testid={`user-numbering-${k}`}
                        checked={effective}
                        disabled={fromRole}
                        onChange={() => {
                          setForm(p => {
                            const cur = { ...((p.permissions || {}).numbering_override || {}) };
                            if (cur[k]) delete cur[k]; else cur[k] = true;
                            return { ...p, permissions: { ...(p.permissions || {}), numbering_override: cur } };
                          });
                        }}
                        className={fromRole ? "accent-slate-400" : "accent-amber-600"}
                      />
                      <span className="text-slate-700 truncate flex-1">{label}</span>
                      {fromRole && (
                        <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1 py-0.5" title="Granted by role">
                          via role
                        </span>
                      )}
                      {!fromRole && fromUser && (
                        <span className="text-[9px] uppercase tracking-wider bg-amber-200 text-amber-900 px-1 py-0.5" title="Per-user exception">
                          per-user
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {form.role === "admin" && (
            <div className="border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
              ✓ This user is in the <strong>admin</strong> role — they have voucher numbering override permission for every module automatically.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-user-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RoleModal({ role, onClose, onSaved }) {
  const [form, setForm] = useState(role || { name: "", description: "", permissions: {} });
  const [saving, setSaving] = useState(false);
  // Sprint 2026-Mar-01 — Permission-matrix filter. Case-insensitive
  // substring match against module key, human label, or section header.
  const [permQuery, setPermQuery] = useState("");

  function toggle(mod, action) {
    const cur = new Set(form.permissions[mod] || []);
    if (cur.has(action)) cur.delete(action); else cur.add(action);
    setForm({ ...form, permissions: { ...form.permissions, [mod]: Array.from(cur) } });
  }

  // Filter MODULE_DEFS by search term. Empty query returns the full list.
  const q = permQuery.trim().toLowerCase();
  const filteredModules = q
    ? MODULE_DEFS.filter(([mod, label, section]) =>
        String(mod).toLowerCase().includes(q)
        || String(label).toLowerCase().includes(q)
        || String(section).toLowerCase().includes(q))
    : MODULE_DEFS;
  // Only render sections that still have at least one matching module.
  const visibleSections = SECTIONS.filter(s =>
    filteredModules.some(([, , sec]) => sec === s));
  const nothingFound = q && filteredModules.length === 0;

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (role) await api.patch(`/roles/${role.id}`, { description: form.description, permissions: form.permissions });
      else await api.post("/roles", { name: form.name.toLowerCase(), description: form.description, permissions: form.permissions });
      toast.success("Saved"); onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-30 shadow-sm">
          <h2 className="font-display text-xl font-bold">{role ? "Edit Role" : "New Role"}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Role Name *"><input data-testid="role-name" required disabled={!!role} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls + " disabled:bg-slate-100"} /></Field>
            <Field label="Description"><input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} className={inputCls} /></Field>
          </div>
          <div className="border border-slate-200">
            {/* Sprint 2026-Mar-01 — Search filter for the permission
                matrix. As roles get more modules, scrolling the whole
                table becomes tedious — this narrows the list live. */}
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 sticky top-[57px] z-30">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">
                Filter modules
              </span>
              <input
                type="search"
                value={permQuery}
                onChange={e => setPermQuery(e.target.value)}
                placeholder="Search by module, label, or section…"
                className="flex-1 border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-orange-500"
                data-testid="role-perm-search"
              />
              {permQuery && (
                <>
                  <span className="text-[10px] text-slate-500 whitespace-nowrap">
                    {filteredModules.length} of {MODULE_DEFS.length}
                  </span>
                  <button type="button" onClick={() => setPermQuery("")}
                          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-800 px-1"
                          data-testid="role-perm-search-clear">Clear</button>
                </>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b-2 border-slate-200 sticky top-[100px] z-20 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                <tr><th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3 sticky left-0 bg-slate-50">Module</th>
                  {ACTIONS.map(a => <th key={a} className="text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3 whitespace-nowrap bg-slate-50">{ACTION_LABELS[a] || a}</th>)}
                </tr>
              </thead>
              <tbody>
                {nothingFound && (
                  <tr data-testid="role-perm-empty">
                    <td colSpan={ACTIONS.length + 1}
                        className="p-6 text-center text-xs text-slate-400 italic">
                      No modules match “{permQuery}”.
                    </td>
                  </tr>
                )}
                {visibleSections.map(section => (
                  <React.Fragment key={section}>
                    <tr className="bg-orange-50">
                      <td colSpan={ACTIONS.length + 1} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-[#F97316]">
                        {section}
                      </td>
                    </tr>
                    {filteredModules.filter(([, , s]) => s === section).map(([mod, label]) => {
                      const acts = form.permissions[mod] || [];
                      const extraActs = MODULE_EXTRA_ACTIONS[mod] || [];
                      const allActs = [...ACTIONS, ...extraActs];
                      const allOn = allActs.every(a => acts.includes(a));
                      return (
                        <React.Fragment key={mod}>
                        <tr className="border-b border-slate-100">
                          <td className="py-2 px-3 text-sm font-medium text-slate-700 sticky left-0 bg-white flex items-center justify-between gap-2">
                            <span>{label}</span>
                            <button
                              type="button"
                              data-testid={`toggle-all-${mod}`}
                              onClick={() => {
                                const newActs = allOn ? [] : [...allActs];
                                setForm({ ...form, permissions: { ...form.permissions, [mod]: newActs } });
                              }}
                              className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${allOn ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                              {allOn ? "All" : "None"}
                            </button>
                          </td>
                          {ACTIONS.map(a => (
                            <td key={a} className="text-center">
                              <input data-testid={`perm-${mod}-${a}`} type="checkbox" checked={acts.includes(a)} onChange={() => toggle(mod, a)} className="accent-[#F97316]" />
                            </td>
                          ))}
                        </tr>
                        {extraActs.length > 0 && (
                          <tr className="border-b border-slate-100 bg-sky-50/40">
                            <td colSpan={ACTIONS.length + 1} className="py-1.5 px-3">
                              <div className="flex flex-wrap items-center gap-4 pl-4">
                                <span className="text-[9px] uppercase tracking-wider font-bold text-sky-700">Granular:</span>
                                {extraActs.map(a => (
                                  <label key={a} className="flex items-center gap-1.5 text-xs text-slate-600">
                                    <input data-testid={`perm-${mod}-${a}`} type="checkbox" checked={acts.includes(a)} onChange={() => toggle(mod, a)} className="accent-[#F97316]" />
                                    {EXTRA_ACTION_LABELS[a] || a}
                                  </label>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* ---- Voucher Numbering Override panel ---- */}
          <div className="border border-amber-200 bg-amber-50/30">
            <div className="bg-amber-50 px-3 py-2 flex items-center justify-between border-b border-amber-200">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Voucher Numbering Override</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  Tick the voucher types this role can unlock and manually re-number. Admin role gets all modules automatically.
                </div>
              </div>
              {form.name !== "admin" && (
                <div className="flex items-center gap-2">
                  <button type="button"
                    data-testid="role-numbering-select-all"
                    onClick={() => setForm(p => ({
                      ...p,
                      permissions: {
                        ...p.permissions,
                        numbering_override: Object.fromEntries(NUMBERING_MODULES.map(([k]) => [k, true])),
                      },
                    }))}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline">
                    All
                  </button>
                  <span className="text-slate-300">·</span>
                  <button type="button"
                    data-testid="role-numbering-clear-all"
                    onClick={() => setForm(p => ({
                      ...p,
                      permissions: { ...p.permissions, numbering_override: {} },
                    }))}
                    className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:underline">
                    None
                  </button>
                </div>
              )}
            </div>
            {form.name === "admin" ? (
              <div className="px-3 py-3 text-[11px] text-emerald-800 bg-emerald-50">
                ✓ Admin role implicitly has override permission for every voucher type — no per-module config required.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 px-3 py-2">
                {NUMBERING_MODULES.map(([k, label]) => {
                  const map = (form.permissions || {}).numbering_override || {};
                  const checked = !!map[k];
                  return (
                    <label key={k} className="flex items-center gap-2 text-xs hover:bg-amber-50/60 px-2 py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`role-numbering-${k}`}
                        checked={checked}
                        onChange={() => {
                          setForm(p => {
                            const cur = { ...((p.permissions || {}).numbering_override || {}) };
                            if (checked) delete cur[k]; else cur[k] = true;
                            return { ...p, permissions: { ...p.permissions, numbering_override: cur } };
                          });
                        }}
                        className="accent-amber-600"
                      />
                      <span className="text-slate-700">{label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-role-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
